
import { useState, useEffect } from "react"
import { Building2, Users, Car, Mail, Phone, Key, Calendar } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tables } from "@/integrations/supabase/types"
import { supabase } from "@/integrations/supabase/client"

type Unit = Tables<"units">
type UnitDetails = Tables<"unit_details">

interface DetalhesUnidadeModalProps {
  unit: Unit & {
    owner?: { full_name?: string } | null
    unit_details?: UnitDetails | null
  }
  children: React.ReactNode
}

const DetalhesUnidadeModal = ({ unit, children }: DetalhesUnidadeModalProps) => {
  const [open, setOpen] = useState(false)
  const [unitDetails, setUnitDetails] = useState<UnitDetails | null>(unit.unit_details || null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open && unit.id && !unitDetails) {
      loadUnitDetails()
    }
  }, [open, unit.id])

  const loadUnitDetails = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from("unit_details")
        .select("*")
        .eq("unit_id", unit.id)
        .maybeSingle()

      if (error) {
        console.error("Erro ao carregar detalhes:", error)
        return
      }

      if (data) {
        setUnitDetails(data)
      }
    } catch (error) {
      console.error("Erro ao carregar detalhes da unidade:", error)
    } finally {
      setLoading(false)
    }
  }

  const unitNumber = unit.building ? `${unit.building}-${unit.number}` : unit.number

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-ita-blue" />
            {unitDetails?.unit_type === 'casa' ? 'Casa' : 'Apartamento'} {unitNumber}
          </DialogTitle>
          <DialogDescription>
            Informações completas da unidade
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8">Carregando informações...</div>
        ) : (
          <div className="space-y-6">
            {/* Informações Básicas */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Informações da Unidade</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">Tipo:</span>
                  <p className="font-medium">
                    {unitDetails?.unit_type === 'casa' ? 'Casa' : 'Apartamento'}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Número:</span>
                  <p className="font-medium">{unitNumber}</p>
                </div>
                {unit.floor && (
                  <div>
                    <span className="text-sm text-gray-600">Andar:</span>
                    <p className="font-medium">{unit.floor}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Proprietário */}
            {unitDetails?.owner_name && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5 text-ita-blue" />
                  Proprietário
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p className="font-medium text-lg">{unitDetails.owner_name}</p>
                  {unitDetails.owner_email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="h-4 w-4" />
                      {unitDetails.owner_email}
                    </div>
                  )}
                  {unitDetails.owner_phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="h-4 w-4" />
                      {unitDetails.owner_phone}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Locatário */}
            {unitDetails?.tenant_name && (
              <div className="space-y-3 border-t pt-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Key className="h-5 w-5 text-orange-500" />
                  Locatário
                </h3>
                <div className="bg-orange-50 p-4 rounded-lg space-y-2">
                  <p className="font-medium text-lg">{unitDetails.tenant_name}</p>
                  {unitDetails.tenant_email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="h-4 w-4" />
                      {unitDetails.tenant_email}
                    </div>
                  )}
                  {unitDetails.tenant_phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="h-4 w-4" />
                      {unitDetails.tenant_phone}
                    </div>
                  )}
                  {unitDetails.tenant_start_date && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      Contrato: {new Date(unitDetails.tenant_start_date).toLocaleDateString()}
                      {unitDetails.tenant_end_date && ` até ${new Date(unitDetails.tenant_end_date).toLocaleDateString()}`}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Familiares */}
            {unitDetails?.family_members && Array.isArray(unitDetails.family_members) && unitDetails.family_members.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">Familiares</h3>
                <div className="space-y-2">
                  {(unitDetails.family_members as any[]).map((member: any, index: number) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <span className="font-medium">{member.name}</span>
                      <Badge variant="secondary">{member.relationship}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Veículos */}
            {unitDetails?.vehicles && Array.isArray(unitDetails.vehicles) && unitDetails.vehicles.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Car className="h-5 w-5 text-ita-blue" />
                  Veículos
                </h3>
                <div className="space-y-2">
                  {(unitDetails.vehicles as any[]).map((vehicle: any, index: number) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">
                            {vehicle.brand} {vehicle.model}
                          </p>
                          <p className="text-sm text-gray-600">
                            Cor: {vehicle.color}
                          </p>
                        </div>
                        <Badge variant="outline" className="font-mono">
                          {vehicle.plate}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!unitDetails && (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Nenhuma informação adicional cadastrada para esta unidade.</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default DetalhesUnidadeModal
